<template>
    <div class="p-2 sm:p-3 md:p-6">
        <div class="mb-2 sm:mb-3 md:mb-4">
            <h1 class="text-sm sm:text-base font-bold text-gray-800">문의 목록</h1>
            <p class="text-[10px] sm:text-xs text-gray-600 mt-0.5">매장 관련 문의 내역을 확인할 수 있습니다.</p>
        </div>

        <!-- 검색 및 필터 영역 -->
        <div class="flex justify-between items-center gap-2 sm:gap-4 mb-3">
            <div class="flex rounded-lg border border-gray-300 overflow-hidden">
                <button @click="viewType = 'card'"
                    :class="['px-2 sm:px-3 py-1.5 sm:py-2 text-[10px] sm:text-xs transition-colors flex items-center gap-1', viewType === 'card' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50']">
                    <svg class="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z">
                        </path>
                    </svg>
                </button>
                <button @click="viewType = 'list'"
                    :class="['px-2 sm:px-3 py-1.5 sm:py-2 text-[10px] sm:text-xs transition-colors flex items-center gap-1', viewType === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50']">
                    <svg class="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M4 6h16M4 10h16M4 14h16M4 18h16">
                        </path>
                    </svg>
                </button>
            </div>
            <div class="flex items-center gap-2 sm:gap-4">
                <div class="relative">
                    <select v-model="statusFilter"
                        class="appearance-none pl-2.5 sm:pl-3 pr-7 sm:pr-8 py-1.5 sm:py-2 text-[10px] sm:text-xs bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-gray-50 transition-colors cursor-pointer">
                        <option value="">전체 상태</option>
                        <option value="125">안읽음</option>
                        <option value="126">읽음</option>
                        <option value="128">완료</option>
                    </select>
                    <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>
                <button @click="openNewInquiryModal"
                    class="px-2.5 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    새 문의하기
                </button>
            </div>
        </div>

        <!-- 카드 뷰 -->
        <div v-if="viewType === 'card'" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
            <template v-if="isLoading">
                <div class="col-span-full flex justify-center items-center py-12">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-4 border-gray-300"></div>
                </div>
            </template>
            <template v-else>
                <div v-for="inquiry in inquiries" :key="inquiry.id"
                    class="bg-white rounded-lg shadow overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                    @click="openInquiryDetail(inquiry)">
                    <div class="p-2 sm:p-2.5 md:p-4">
                        <div class="flex justify-between items-start gap-3">
                            <div class="flex-1 min-w-0">
                                <!-- 브랜드 토글 영역 -->
                                <div class="flex flex-wrap items-center gap-2 mb-2">
                                    <label class="flex items-center gap-1.5">
                                        <input type="checkbox" :checked="inquiry.mr_agabang" disabled
                                            class="w-3.5 h-3.5 text-pink-600 rounded border-gray-300 focus:ring-pink-500">
                                        <span class="text-[10px] sm:text-xs text-gray-700">아가방</span>
                                    </label>
                                    <label class="flex items-center gap-1.5">
                                        <input type="checkbox" :checked="inquiry.mr_ettoi" disabled
                                            class="w-3.5 h-3.5 text-purple-600 rounded border-gray-300 focus:ring-purple-500">
                                        <span class="text-[10px] sm:text-xs text-gray-700">에뜨와</span>
                                    </label>
                                    <label class="flex items-center gap-1.5">
                                        <input type="checkbox" :checked="inquiry.mr_disney" disabled
                                            class="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500">
                                        <span class="text-[10px] sm:text-xs text-gray-700">디즈니</span>
                                    </label>
                                </div>
                                <div
                                    class="flex flex-wrap items-center gap-x-1 gap-y-0.5 mb-1.5 text-[10px] sm:text-xs text-gray-600">
                                    <span>{{ inquiry.cat_name }}</span>
                                    <span class="text-gray-400">&gt;</span>
                                    <span>{{ inquiry.midcat_name }}</span>
                                    <template v-if="inquiry.midcat_name !== inquiry.subcat_name">
                                        <span class="text-gray-400">&gt;</span>
                                        <span>{{ inquiry.subcat_name }}</span>
                                    </template>
                                </div>
                                <h3 class="text-xs sm:text-sm font-medium text-gray-900 mb-1 truncate">{{ inquiry.title
                                }}</h3>
                                <div class="flex items-center gap-2 text-[10px] sm:text-xs text-gray-500">
                                    <span class="text-[10px] sm:text-xs text-gray-500">{{ formatDate(inquiry.reg_date,
                                        true) }}</span>
                                </div>
                            </div>
                            <div class="flex-shrink-0 flex flex-col items-end gap-2">
                                <span :class="[
                                    'px-2 py-0.5 text-[10px] sm:text-xs rounded-full whitespace-nowrap',
                                    inquiry.status_text === '안읽음' ? 'bg-gray-100 text-gray-600' :
                                        inquiry.status_text === '읽음' ? 'bg-gray-100 text-gray-600' :
                                            'bg-green-100 text-green-800'
                                ]">
                                    {{ inquiry.status_text }}
                                </span>
                                <div class="flex items-center gap-2 text-[10px] sm:text-xs text-gray-500">
                                    <span v-if="inquiry.attachment > 0" class="flex items-center gap-1">
                                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13">
                                            </path>
                                        </svg>
                                        {{ inquiry.attachment }}
                                    </span>
                                    <span v-if="inquiry.comment_count > 0" class="flex items-center gap-1">
                                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z">
                                            </path>
                                        </svg>
                                        {{ inquiry.comment_count }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="inquiry.latest_comment" class="bg-gray-50 p-2 sm:p-2.5 md:p-3 border-t border-gray-100">
                        <div class="flex flex-col gap-1">
                            <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span
                                    class="text-[10px] sm:text-xs px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded-full">
                                    {{ inquiry.latest_comment_admin_name ? `본사 답변
                                    (${inquiry.latest_comment_admin_name})` : '내 답변' }}
                                </span>
                                <span class="text-[10px] sm:text-xs text-gray-500">{{
                                    formatDate(inquiry.latest_comment_date, false)
                                }}</span>
                            </div>
                            <p class="text-[10px] sm:text-xs text-gray-700 truncate">{{ inquiry.latest_comment }}</p>
                        </div>
                    </div>
                    <div v-else class="bg-gray-50 p-2 sm:p-2.5 md:p-3 border-t border-gray-100">
                        <div class="text-[10px] sm:text-xs text-gray-400">답변 대기중</div>
                    </div>
                </div>
            </template>
        </div>

        <!-- 리스트 뷰 -->
        <div v-else class="bg-white rounded-lg shadow overflow-hidden">
            <table class="w-full">
                <thead class="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <th class="px-2 sm:px-3 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500">브랜드
                        </th>
                        <th
                            class="hidden md:table-cell px-3 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500">
                            카테고리</th>
                        <th class="px-2 sm:px-3 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500">제목</th>
                        <th class="px-2 sm:px-3 py-2 text-center text-[10px] sm:text-xs font-medium text-gray-500">상태
                        </th>
                        <th
                            class="hidden sm:table-cell px-3 py-2 text-center text-[10px] sm:text-xs font-medium text-gray-500">
                            첨부/댓글</th>
                        <th class="px-2 sm:px-3 py-2 text-center text-[10px] sm:text-xs font-medium text-gray-500">등록일
                        </th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                    <template v-if="isLoading">
                        <tr>
                            <td colspan="6" class="px-4 py-8 text-center">
                                <div class="flex justify-center items-center">
                                    <div class="animate-spin rounded-full h-8 w-8 border-b-4 border-gray-300"></div>
                                </div>
                            </td>
                        </tr>
                    </template>
                    <template v-else>
                        <tr v-for="inquiry in inquiries" :key="inquiry.id" class="hover:bg-gray-50 cursor-pointer"
                            @click="openInquiryDetail(inquiry)">
                            <td class="px-2 sm:px-3 py-2">
                                <div class="flex flex-col gap-1 text-[10px] sm:text-xs text-gray-600">
                                    <span v-if="inquiry.mr_agabang">아가방</span>
                                    <span v-if="inquiry.mr_ettoi">에뜨와</span>
                                    <span v-if="inquiry.mr_disney">디즈니</span>
                                </div>
                            </td>
                            <td class="hidden md:table-cell px-3 py-2">
                                <div class="flex items-center text-xs text-gray-600">
                                    <span>{{ inquiry.cat_name }}</span>
                                    <span class="mx-1 text-gray-400">&gt;</span>
                                    <span>{{ inquiry.midcat_name }}</span>
                                    <template v-if="inquiry.midcat_name !== inquiry.subcat_name">
                                        <span class="mx-1 text-gray-400">&gt;</span>
                                        <span>{{ inquiry.subcat_name }}</span>
                                    </template>
                                </div>
                            </td>
                            <td class="px-2 sm:px-3 py-2">
                                <div
                                    class="text-[10px] sm:text-xs text-gray-900 truncate max-w-[120px] sm:max-w-[200px] md:max-w-[300px]">
                                    {{ inquiry.title }}
                                </div>
                            </td>
                            <td class="px-2 sm:px-3 py-2 text-center">
                                <span :class="[
                                    'px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs rounded-full whitespace-nowrap',
                                    inquiry.status_text === '안읽음' ? 'bg-gray-100 text-gray-600' :
                                        inquiry.status_text === '읽음' ? 'bg-gray-100 text-gray-600' :
                                            'bg-green-100 text-green-800'
                                ]">
                                    {{ inquiry.status_text }}
                                </span>
                            </td>
                            <td class="hidden sm:table-cell px-3 py-2 text-center">
                                <div class="flex items-center justify-center gap-2 text-xs text-gray-500">
                                    <span v-if="inquiry.attachment > 0" class="flex items-center gap-1">
                                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13">
                                            </path>
                                        </svg>
                                        {{ inquiry.attachment }}
                                    </span>
                                    <span v-if="inquiry.comment_count > 0" class="flex items-center gap-1">
                                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z">
                                            </path>
                                        </svg>
                                        {{ inquiry.comment_count }}
                                    </span>
                                </div>
                            </td>
                            <td class="px-2 sm:px-3 py-2 text-center">
                                <span class="text-[10px] sm:text-xs text-gray-500 hidden sm:inline">{{
                                    formatDate(inquiry.reg_date, true) }}</span>
                                <span class="text-[10px] sm:text-xs text-gray-500 sm:hidden">{{
                                    formatDateShort(inquiry.reg_date) }}</span>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>

        <!-- 페이지네이션 -->
        <div class="flex justify-center mt-3 sm:mt-4 md:mt-6">
            <v-pagination v-model="currentPage" :length="totalPages" :total-visible="getTotalVisible" density="compact"
                color="grey-darken-1" class="pagination-text" size="small"
                @update:model-value="handlePageChange"></v-pagination>
        </div>

        <!-- 새 문의하기 모달 -->
        <div v-if="showInquiryModal" class="fixed inset-0 z-[70]" aria-labelledby="modal-title" role="dialog"
            aria-modal="true">
            <!-- 모달 백드롭 -->
            <div class="fixed inset-0 bg-gray-500 bg-opacity-75" aria-hidden="true" @click="closeInquiryModal">
            </div>

            <!-- 모달 컨테이너 -->
            <div class="fixed inset-0 flex items-center justify-center" @click="closeInquiryModal">
                <div class="w-full max-w-2xl bg-white" @click.stop>
                    <!-- 모달 내용 -->
                    <InquiryForm :editData="editingInquiry" @close="closeInquiryModal" @submit="handleInquirySubmit" />
                </div>
            </div>
        </div>

        <!-- 문의 상세보기 모달 -->
        <div v-if="selectedInquiry" class="fixed inset-0 z-50" aria-labelledby="modal-title" role="dialog"
            aria-modal="true">
            <!-- 모달 백드롭 -->
            <div class="fixed inset-0 bg-gray-500 bg-opacity-75" aria-hidden="true" @click="closeInquiryDetail">
            </div>

            <!-- 모달 컨테이너 -->
            <div class="fixed inset-0 flex items-center justify-center p-4" @click="closeInquiryDetail">
                <div @click.stop>
                    <InquiryDetail :inquiry="selectedInquiry" @close="closeInquiryDetail"
                        @submit-comment="handleCommentSubmit" @edit="handleInquiryEdit"
                        @refresh-list="handleRefreshList" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, onUnmounted } from 'vue';
import inquiryAPI from '@/api/inquiry/inquiry';
import InquiryForm from '@/components/inquiry/InquiryForm.vue';
import InquiryDetail from '@/components/inquiry/InquiryDetail.vue';

// 상태 관리
const statusFilter = ref('');
const currentPage = ref(1);
const itemsPerPage = 10;
const inquiries = ref([]);
const totalCount = ref(0);
const showInquiryModal = ref(false);
const viewType = ref('card');
const selectedInquiry = ref(null);
const editingInquiry = ref(null);
const isLoading = ref(false);
let loadingTimeout = null;

// 데이터 로딩
const loadInquiries = async () => {
    isLoading.value = true;
    
    try {
        const response = await inquiryAPI.getInquiries({
            page: currentPage.value,
            limit: itemsPerPage,
            status: statusFilter.value
        });
        inquiries.value = response.data.items;
        totalCount.value = response.data.total_count;
    } catch (error) {
        console.error('문의 목록을 불러오는데 실패했습니다:', error);
    } finally {
        isLoading.value = false;
    }
};

// 총 페이지 수 계산
const totalPages = computed(() => {
    return Math.ceil(totalCount.value / itemsPerPage);
});

// 날짜 포맷 함수
const formatDate = (dateString, includeYear = true) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ko-KR', {
        year: includeYear ? 'numeric' : undefined,
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date);
};

// 짧은 날짜 포맷 함수 (월/일만)
const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ko-KR', {
        month: '2-digit',
        day: '2-digit'
    }).format(date);
};

const openNewInquiryModal = () => {
    showInquiryModal.value = true;
};

const closeInquiryModal = () => {
    showInquiryModal.value = false;
};

const handleInquiryEdit = async (inquiry) => {
    try {
        // 수정 데이터 설정
        editingInquiry.value = {
            ...inquiry,
            id: inquiry.id,
            cat_id: inquiry.cat_id,
            midcat_id: inquiry.midcat_id,
            subcat_id: inquiry.subcat_id,
            selectedBrand: inquiry.mr_agabang ? 'agabang' : 
                          inquiry.mr_ettoi ? 'ettoi' : 'disney',
            files: inquiry.attachments?.map(file => ({
                name: file.name,
                size: 0,
                file: null,
                originalFile: null,
                isExisting: true,
                existingFile: file
            })) || []
        };
        
        // 수정 모달 표시 (상세보기 모달은 닫지 않음)
        showInquiryModal.value = true;
    } catch (error) {
        console.error('문의 수정 데이터 로드 실패:', error);
    }
};

const handleInquirySubmit = async () => {
    await loadInquiries();
    closeInquiryModal();
    editingInquiry.value = null;
    // 상세보기 모달도 닫기
    closeInquiryDetail();
};

const openInquiryDetail = (inquiry) => {
    selectedInquiry.value = inquiry;
    // 현재 URL에 상태 추가
    history.pushState({ type: 'inquiry-detail' }, '');
};

const closeInquiryDetail = () => {
    selectedInquiry.value = null;
};

// 뒤로가기 이벤트 핸들러
const handlePopState = () => {
    if (selectedInquiry.value) {
        selectedInquiry.value = null;
    }
};

const handleCommentSubmit = async (formData) => {
    try {
        await inquiryAPI.addComment(selectedInquiry.value.id, formData);
        await loadInquiries();
        // 댓글이 추가된 문의를 다시 선택하여 상세 정보 업데이트
        const updatedInquiry = inquiries.value.find(i => i.id === selectedInquiry.value.id);
        if (updatedInquiry) {
            selectedInquiry.value = updatedInquiry;
        }
    } catch (error) {
        console.error('댓글 등록 실패:', error);
    }
};

const handleRefreshList = async () => {
    await loadInquiries();
    closeInquiryDetail();
};

// 감시자 설정
watch([currentPage, statusFilter], () => {
    loadInquiries();
});

// 컴포넌트 마운트 시 데이터 로딩 및 이벤트 리스너 등록
onMounted(() => {
    loadInquiries();
    window.addEventListener('popstate', handlePopState);
});

// 컴포넌트 언마운트 시 이벤트 리스너 제거
onUnmounted(() => {
    window.removeEventListener('popstate', handlePopState);
    if (loadingTimeout) {
        clearTimeout(loadingTimeout);
    }
});

const getTotalVisible = computed(() => {
    const width = window.innerWidth;
    if (width >= 1024) return 7;
    if (width >= 768) return 5;
    return 3;
});

const handlePageChange = (page) => {
    currentPage.value = page;
    loadInquiries();
};
</script>

<style scoped>
/* ... 기존 스타일 ... */
@media (min-width: 640px) {
    :deep(.pagination-text) {
        font-size: 12px;
    }

    :deep(.pagination-text .v-btn) {
        font-size: 12px;
    }
}

@media (min-width: 1024px) {
    :deep(.pagination-text) {
        font-size: 14px;
    }

    :deep(.pagination-text .v-btn) {
        font-size: 14px;
    }
}

@media (min-width: 1280px) {
    :deep(.pagination-text) {
        font-size: 15px;
    }

    :deep(.pagination-text .v-btn) {
        font-size: 15px;
    }
}
</style>